// Traduce la respuesta de afip-consulta (constancia de ARCA + padron local)
// a los campos del formulario de clientes.
import provinces from "../constants/provinces";

const normalizar = (texto) =>
  String(texto || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // acentos
    .toUpperCase()
    .replace(/\bDEL?\b/g, " ") // "CIUDAD AUTONOMA DE BUENOS AIRES" == "... BUENOS AIRES"
    .replace(/[^A-Z0-9]+/g, " ")
    .trim();

/**
 * Provincia de ARCA (texto libre) -> id de src/constants/provinces.
 * ARCA agrega colas largas ("TIERRA DEL FUEGO, ANTARTIDA E ISLAS..."), asi que
 * si no hay coincidencia exacta probamos que una contenga a la otra.
 */
export const matchProvinceId = (descripcion) => {
  const buscada = normalizar(descripcion);
  if (!buscada) return "";

  const exacta = provinces.find((p) => normalizar(p.name) === buscada);
  if (exacta) return exacta.id;

  const parcial = provinces.find((p) => {
    const nombre = normalizar(p.name);
    if (nombre.length < 5) return false; // "Otro" no puede ganar por contencion
    return buscada.includes(nombre) || nombre.includes(buscada);
  });

  return parcial ? parcial.id : "";
};

const IMPUESTO_IVA = "30";
const IMPUESTO_IVA_EXENTO = "32";
const IMPUESTO_MONOTRIBUTO = "20";

// ARCA devuelve el estado como codigo corto: AC (activo), BA (baja), EX (exento).
const impuestoActivo = (impuestos, id) =>
  (impuestos || []).some(
    (i) => String(i?.id) === id && /^AC/i.test(String(i?.estado || "AC"))
  );

/** Condicion frente al IVA en el formato que guarda el sistema: RI / M / E. */
export const inferirCondicionIva = ({ arca, padron }) => {
  if (arca) {
    if (arca.monotributo?.categoria || impuestoActivo(arca.impuestos, IMPUESTO_MONOTRIBUTO)) {
      return "M";
    }
    if (impuestoActivo(arca.impuestos, IMPUESTO_IVA)) return "RI";
    if (impuestoActivo(arca.impuestos, IMPUESTO_IVA_EXENTO)) return "E";
  }

  if (padron) {
    if (padron.monotributo && padron.monotributo !== "NI") return "M";
    if (padron.iva === "AC" || padron.iva === "AN") return "RI";
    if (padron.iva === "EX" || padron.iva === "XN") return "E";
  }

  return "";
};

const partirDenominacion = (denominacion) => {
  // El padron guarda "APELLIDO NOMBRE" en un solo campo para personas fisicas.
  const partes = String(denominacion || "").trim().split(/\s+/);
  if (partes.length < 2) return { lastName: "", name: "" };
  return { lastName: partes[0], name: partes.slice(1).join(" ") };
};

/**
 * Campos a autocompletar en el formulario.
 *
 * Devuelve SIEMPRE todas las claves, con "" cuando la fuente no tiene el dato.
 * Es a proposito: si se omitieran, al consultar un CUIT nuevo quedarian los
 * valores del anterior (una persona juridica no trae nombre ni apellido, y se
 * guardaria la empresa con el contacto de otro cliente).
 */
export const mapAfipToClient = (respuesta) => {
  const arca = respuesta?.arca || null;
  const padron = respuesta?.padron || null;
  const domicilio = arca?.domicilio || {};

  const esFisica = /FISICA/i.test(arca?.tipo_persona || "");
  const esJuridica = /JURIDICA/i.test(arca?.tipo_persona || "");
  const razonSocial = arca?.razon_social || padron?.denominacion || "";

  let name = arca?.nombre || "";
  let lastName = arca?.apellido || "";

  // Si ARCA no separo nombre y apellido, lo intentamos con la denominacion del
  // padron.
  if (!name && !lastName && esFisica) {
    ({ name, lastName } = partirDenominacion(razonSocial));
  }

  // Persona juridica: ARCA no devuelve una persona, asi que se copia la razon
  // social en Nombre y Apellido para que los campos no queden vacios. El
  // operador los cambia por la persona de contacto si la conoce.
  if (esJuridica && !name && !lastName) {
    name = razonSocial;
    lastName = razonSocial;
  }

  return {
    company: razonSocial,
    name,
    lastName,
    address: domicilio.direccion || "",
    province: matchProvinceId(domicilio.provincia),
    ivaCondition: inferirCondicionIva({ arca, padron }),
  };
};

/** True si el CUIT corresponde a una persona juridica segun ARCA. */
export const esPersonaJuridica = (respuesta) =>
  /JURIDICA/i.test(respuesta?.arca?.tipo_persona || "");

/** Resumen legible para mostrar debajo del CUIT lo que trajo la consulta. */
export const resumirAfip = (respuesta) => {
  const arca = respuesta?.arca || null;
  const padron = respuesta?.padron || null;
  const domicilio = arca?.domicilio || {};
  const actividad = (arca?.actividades || [])[0];

  const fuente =
    respuesta?.fuente === "tusfacturas"
      ? "ARCA vía tusfacturas.app"
      : arca
        ? "ARCA — constancia de inscripción"
        : "ARCA — padrón descargable";

  return {
    fuente,
    razonSocial: arca?.razon_social || padron?.denominacion || "-",
    tipoPersona: arca?.tipo_persona || null,
    estadoClave: arca?.estado_clave || null,
    domicilio: domicilio.completo || null,
    condicionIva: inferirCondicionIva({ arca, padron }),
    actividad: actividad ? actividad.descripcion : null,
    categoriaMonotributo: arca?.monotributo?.categoria || null,
  };
};

export const ETIQUETA_IVA = {
  RI: "Responsable Inscripto",
  M: "Monotributista",
  E: "Exento",
};
